import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import PresenceBar from './components/PresenceBar';
import SessionListPanel from './components/SessionListPanel';
import SessionTimeline from './components/SessionTimeline';
import SessionComposer from './components/SessionComposer';
import SessionSidePanel from './components/SessionSidePanel';
import TransferModal from './components/TransferModal';
import { LIST_TABS } from './csConstants';
import useCsAdminSse from './useCsAdminSse';
import './csWorkbench.css';

function mergeBySeq(prev, next) {
  const map = new Map();
  [...(prev || []), ...(next || [])].forEach((m) => {
    const key = m.serverMessageId || `seq-${m.seq}`;
    map.set(key, m);
  });
  return Array.from(map.values()).sort((a, b) => (a.seq || 0) - (b.seq || 0));
}

export default function CsWorkbenchPage() {
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState(LIST_TABS.QUEUE);
  const [queueItems, setQueueItems] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [endedSessions, setEndedSessions] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [selected, setSelected] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgMeta, setMsgMeta] = useState({ hasMoreBefore: false, oldestSeq: null, newestSeq: null });
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState('');
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [invitingRating, setInvitingRating] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [banner, setBanner] = useState('');
  const [auditSummary, setAuditSummary] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [pendingSoftAssigns, setPendingSoftAssigns] = useState([]);
  const [supportMode, setSupportMode] = useState('FORMOSA_IM');
  const [featureLoaded, setFeatureLoaded] = useState(false);

  const formosaImOnline = supportMode === 'FORMOSA_IM';

  const selectedRef = useRef(selected);
  const tabRef = useRef(tab);
  const msgMetaRef = useRef(msgMeta);
  selectedRef.current = selected;
  tabRef.current = tab;
  msgMetaRef.current = msgMeta;

  const loadLists = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const [queue, mine, ended, pendingAssigns, feature] = await Promise.all([
        adminApi.imCsQueue({ limit: 50 }),
        adminApi.imCsMySessions(),
        adminApi.imCsMyEndedSessions({ limit: 50 }),
        adminApi.imCsPendingSoftAssigns().catch(() => []),
        adminApi.getImFeatureSwitch().catch(() => null),
      ]);
      if (feature?.supportMode) {
        setSupportMode(feature.supportMode);
      }
      setFeatureLoaded(true);
      setQueueItems(queue?.items || []);
      setMySessions(Array.isArray(mine) ? mine : []);
      setEndedSessions(ended?.items || []);
      setPendingSoftAssigns(Array.isArray(pendingAssigns) ? pendingAssigns : []);
    } catch (err) {
      setListError(err.message || '載入列表失敗');
      setFeatureLoaded(true);
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async (sessionId) => {
    if (sessionId == null || sessionId === '') {
      setAuditSummary(null);
      return;
    }
    setAuditLoading(true);
    try {
      const summary = await adminApi.imCsAuditSummary(String(sessionId));
      setAuditSummary(summary);
    } catch {
      setAuditSummary(null);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (sessionId, { beforeSeq, afterSeq } = {}) => {
    if (sessionId == null || sessionId === '') return;
    // Never coerce snowflake sessionId through Number()
    const sid = String(sessionId);
    setMsgLoading(true);
    setMsgError('');
    try {
      const page = await adminApi.imCsListMessages(sid, {
        limit: 50,
        beforeSeq,
        afterSeq,
      });
      const items = page?.items || [];
      if (beforeSeq != null) {
        setMessages((prev) => mergeBySeq(items, prev));
      } else if (afterSeq != null) {
        setMessages((prev) => mergeBySeq(prev, items));
      } else {
        setMessages(items);
      }
      setMsgMeta({
        hasMoreBefore: !!page?.hasMoreBefore,
        oldestSeq: page?.oldestSeq != null ? String(page.oldestSeq) : null,
        newestSeq: page?.newestSeq != null ? String(page.newestSeq) : null,
      });
    } catch (err) {
      setMsgError(err.message || '載入訊息失敗');
    } finally {
      setMsgLoading(false);
    }
  }, []);

  const { connected: sseConnected } = useCsAdminSse({
    onQueueEvent: () => {
      loadLists();
    },
    onSessionStatus: () => {
      loadLists();
    },
    onMessageCreated: (event) => {
      const sid = event?.sessionId != null ? String(event.sessionId) : null;
      const payload = event?.payload;
      const current = selectedRef.current;
      if (!sid || !current?.sessionId || String(current.sessionId) !== sid) {
        if (tabRef.current === LIST_TABS.MINE) loadLists();
        return;
      }
      if (payload?.serverMessageId || payload?.seq != null) {
        setMessages((prev) => mergeBySeq(prev, [payload]));
        if (payload.seq != null) {
          setMsgMeta((m) => ({ ...m, newestSeq: String(payload.seq) }));
        }
        return;
      }
      const meta = msgMetaRef.current;
      if (meta.newestSeq != null) {
        loadMessages(current.sessionId, { afterSeq: meta.newestSeq });
      } else {
        loadMessages(current.sessionId);
      }
    },
  });

  useEffect(() => {
    loadLists();
    // Slow backup poll; SSE drives near-realtime refresh when connected
    const timer = setInterval(loadLists, sseConnected ? 60_000 : 8000);
    return () => clearInterval(timer);
  }, [loadLists, sseConnected]);

  useEffect(() => {
    if (!selected?.sessionId) {
      setMessages([]);
      setAuditSummary(null);
      return undefined;
    }
    loadMessages(selected.sessionId);
    loadAudit(selected.sessionId);
    const ended = selected.status === 'ENDED' || selected.status === 'ABANDONED';
    const timer = setInterval(() => {
      if (sseConnected || ended) return;
      setMsgMeta((meta) => {
        if (meta.newestSeq != null) {
          loadMessages(selected.sessionId, { afterSeq: meta.newestSeq });
        } else {
          loadMessages(selected.sessionId);
        }
        return meta;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [selected?.sessionId, selected?.status, loadMessages, loadAudit, sseConnected]);

  const canCompose = useMemo(() => {
    if (!formosaImOnline) return false;
    if (!selected || !agent) return false;
    if (tab === LIST_TABS.ENDED) return false;
    const mine = selected.agentAdminId == null || selected.agentAdminId === agent.adminUserId
      || selected.agentAdminId === agent.agentId;
    const statusOk = selected.status === 'ACTIVE' || selected.status === 'TRANSFERRING';
    return mine && statusOk && tab === LIST_TABS.MINE;
  }, [selected, agent, tab, formosaImOnline]);

  async function handleClaim(item) {
    setClaimingId(item.sessionId);
    setBanner('');
    try {
      const resp = await adminApi.imCsClaim(item.sessionId, {
        idempotencyKey: `wb-claim-${item.sessionId}-${Date.now()}`,
        expectedSessionVersion: item.sessionVersion,
      });
      setBanner('已接手');
      setTab(LIST_TABS.MINE);
      setSelected({
        ...item,
        ...resp,
        status: resp?.status || 'ACTIVE',
      });
      await loadLists();
    } catch (err) {
      setBanner(err.message || '接手失敗');
    } finally {
      setClaimingId(null);
    }
  }

  async function handleSend(text) {
    if (!selected?.sessionId) return;
    setSending(true);
    setBanner('');
    try {
      const msg = await adminApi.imCsSendMessage(selected.sessionId, {
        text,
        clientMessageId: `wb-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      });
      setMessages((prev) => mergeBySeq(prev, [msg]));
      if (msg?.seq != null) {
        setMsgMeta((m) => ({ ...m, newestSeq: msg.seq }));
      }
    } catch (err) {
      setBanner(err.message || '送出失敗');
    } finally {
      setSending(false);
    }
  }

  async function handleEnd() {
    if (!selected?.sessionId) return;
    if (!window.confirm('確定結束此客服 Session？')) return;
    setEnding(true);
    setBanner('');
    try {
      await adminApi.imCsEndSession(selected.sessionId, {});
      setBanner('Session 已結束');
      setTab(LIST_TABS.ENDED);
      setSelected(null);
      await loadLists();
    } catch (err) {
      setBanner(err.message || '結束失敗');
    } finally {
      setEnding(false);
    }
  }

  async function handleInviteRating() {
    if (!selected?.sessionId) return;
    setInvitingRating(true);
    setBanner('');
    try {
      await adminApi.imCsInviteRating(selected.sessionId);
      setBanner('已邀請會員評價');
    } catch (err) {
      setBanner(err.message || '邀評失敗');
    } finally {
      setInvitingRating(false);
    }
  }

  async function handleAcceptSoftAssign(item) {
    if (!item?.assignRequestId) return;
    setBanner('');
    try {
      await adminApi.imCsAcceptSoftAssign(String(item.assignRequestId));
      setBanner('已接受主管指派');
      setTab(LIST_TABS.MINE);
      await loadLists();
    } catch (err) {
      setBanner(err.message || '接受 Soft Assign 失敗');
    }
  }

  async function handleRejectSoftAssign(item) {
    if (!item?.assignRequestId) return;
    setBanner('');
    try {
      await adminApi.imCsRejectSoftAssign(String(item.assignRequestId));
      setBanner('已拒絕主管指派');
      await loadLists();
    } catch (err) {
      setBanner(err.message || '拒絕 Soft Assign 失敗');
    }
  }

  const selectedStatus = selected
    ? {
        ...selected,
        status: selected.status
          || (tab === LIST_TABS.QUEUE ? 'WAITING' : selected.status),
      }
    : null;

  return (
    <div className="page-card cs-page">
      <div className="section-header">
        <h2>客服工作台</h2>
        <span className="cs-hint">{sseConnected ? '即時連線中' : '輪詢模式'}</span>
      </div>
      <PresenceBar onAgentChange={setAgent} />
      {featureLoaded && !formosaImOnline ? (
        <div className="cs-error" style={{ marginBottom: 12, padding: 12 }}>
          自建客服通道目前維修中或已下線（support.mode={supportMode}）。接手／發訊／轉接已停用；請至「功能開關」確認通道設定。
        </div>
      ) : null}
      {banner ? <div className={banner.includes('失敗') ? 'cs-error' : 'cs-hint'}>{banner}</div> : null}
      {pendingSoftAssigns.length ? (
        <div className="cs-panel" style={{ marginBottom: 12 }}>
          <div className="cs-panel__head">待接受 Soft Assign</div>
          <div className="cs-list">
            {pendingSoftAssigns.map((item) => (
              <div key={item.assignRequestId} className="cs-modal__row">
                <div>
                  <div>{item.sessionNo || item.sessionId}</div>
                  <div className="cs-list-item__meta">
                    {item.sessionStatus || 'ASSIGNED'}
                    {item.expiresAt ? ` · 逾時 ${item.expiresAt}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => handleAcceptSoftAssign(item)}>
                    接受
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleRejectSoftAssign(item)}>
                    拒絕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="cs-workbench">
        <SessionListPanel
          tab={tab}
          onTabChange={setTab}
          queueItems={queueItems}
          mySessions={mySessions}
          endedSessions={endedSessions}
          selectedSessionId={selected?.sessionId}
          onSelect={setSelected}
          onClaim={formosaImOnline ? handleClaim : undefined}
          loading={listLoading}
          error={listError}
          claimingId={formosaImOnline ? claimingId : null}
        />
        <div className="cs-panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <SessionTimeline
            messages={messages}
            loading={msgLoading}
            error={msgError}
            hasMoreBefore={msgMeta.hasMoreBefore}
            onLoadOlder={() => {
              if (msgMeta.oldestSeq != null) {
                loadMessages(selected.sessionId, { beforeSeq: msgMeta.oldestSeq });
              }
            }}
          />
          <SessionComposer enabled={canCompose} sending={sending} onSend={handleSend} />
        </div>
        <SessionSidePanel
          session={selectedStatus}
          agentId={agent?.adminUserId || agent?.agentId}
          ending={ending}
          invitingRating={invitingRating}
          claiming={claimingId != null && claimingId === selected?.sessionId}
          onClaim={
            formosaImOnline && selected && tab !== LIST_TABS.ENDED
              ? () => handleClaim(selected)
              : undefined
          }
          onTransfer={
            formosaImOnline && tab !== LIST_TABS.ENDED ? () => setTransferOpen(true) : undefined
          }
          onEnd={formosaImOnline && tab !== LIST_TABS.ENDED ? handleEnd : undefined}
          onInviteRating={formosaImOnline ? handleInviteRating : undefined}
          auditSummary={auditSummary}
          auditLoading={auditLoading}
        />
      </div>
      <TransferModal
        open={transferOpen}
        sessionId={selected?.sessionId}
        onClose={() => setTransferOpen(false)}
        onTransferred={() => {
          setBanner('已建立轉接請求');
          loadLists();
        }}
      />
    </div>
  );
}
